import { createGlobalState } from '@vueuse/core';
import { ref, watch, type Ref } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { outboundMessagesAnalyticsClient } from '~/services';
import type {
    OutboundMessagesParams,
    Snippet,
} from '~/services/analytics/outboundMessages/outboundMessagesAnalyticsClient.types';

export type SelectedSnippet = Snippet | { snippet_id: number };

export const useAnalyticsSelectSnippets = createGlobalState(() => {
    const teamId = Spark.state.currentTeam.id;
    const { getCurrentGroupId, currentOrgUnit } = useCurrentGroup();

    const cachedSnippets: Ref<Snippet[]> = ref([]);

    const selectingSnippets = ref(false);
    const selectedSnippets: Ref<SelectedSnippet[]> = ref([]);

    watch(
        selectedSnippets,
        () => {
            cachedSnippets.value = cachedSnippets.value.concat(
                selectedSnippets.value.filter(
                    (selected): selected is Snippet =>
                        'snippet_name' in selected &&
                        Boolean(selected.snippet_name) &&
                        !cachedSnippets.value.some((cached) => isSameSnippet(selected, cached))
                )
            );
        },
        {
            deep: true,
        }
    );

    const {
        loading: loadingOptions,
        response: options,
        createSearch,
        cancelKey,
    } = useSearchRequest<Snippet[]>(undefined, 'analytics-snippets-search');

    options.value = [];

    const fetchSelectedSnippets = async (fetchParams: OutboundMessagesParams): Promise<void> => {
        const missingSnippetIds = selectedSnippets.value
            .filter((selected) => !cachedSnippets.value.some((cached) => isSameSnippet(selected, cached)))
            .map(({ snippet_id }) => snippet_id);

        if (missingSnippetIds.length === 0) {
            return;
        }

        const params = {
            range: fetchParams.range,
            snippet_ids: missingSnippetIds,
        };

        const response = await outboundMessagesAnalyticsClient.getListActiveSnippets(
            {
                teamId,
                orgUnitId: currentOrgUnit.value?.id,
                groupId: getCurrentGroupId() ? String(getCurrentGroupId()) : undefined,
            },
            params
        );

        cachedSnippets.value = cachedSnippets.value.concat(response.data.data);

        selectedSnippets.value = selectedSnippets.value.map((selected) => {
            const cached = cachedSnippets.value.find((c) => c.snippet_id === selected.snippet_id);
            return cached || selected;
        });
    };

    const searchFn = createSearch(async (fetchParams: OutboundMessagesParams, query: string) => {
        const params = {
            ...fetchParams,
            filters: {
                ...fetchParams.filters,
                resources: undefined,
                snippet_ids: undefined,
            },
            query: query,
        };

        const response = await outboundMessagesAnalyticsClient.getListActiveSnippets(
            {
                teamId,
                orgUnitId: currentOrgUnit.value?.id,
                groupId: getCurrentGroupId() ? String(getCurrentGroupId()) : undefined,
            },
            params,
            { cancelKey }
        );

        if (!query) {
            fetchSelectedSnippets(fetchParams);
        }

        return response.data.data;
    });

    const isSameSnippet = (a: SelectedSnippet, b: SelectedSnippet): boolean => a.snippet_id === b.snippet_id;

    const snippetName = (snippet: SelectedSnippet): string | undefined =>
        cachedSnippets.value.find((option) => isSameSnippet(snippet, option))?.snippet_name;

    const getSnippetById = (snippetId: number): SelectedSnippet | undefined =>
        cachedSnippets.value.find((option) => isSameSnippet({ snippet_id: snippetId }, option));

    return {
        selectingSnippets,
        selectedSnippets,
        loadingOptions,
        options,
        searchFn,
        isSameSnippet,
        snippetName,
        getSnippetById,
    };
});
